import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { Container } from "../layout/Container.jsx";
import { fetchGallery } from "../../api/public.api.js";
import { assetUrl } from "../../api/client.js";
import { ImageWithSkeleton } from "../shared/ImageWithSkeleton.jsx";

/**
 * Homepage Interior gallery — pulls EVERY Interior photo from the
 * admin-managed Gallery collection (no `isFeatured` filter), then
 * randomly picks up to six per page-load to fill the mosaic. Title
 * overlays are intentionally omitted so the imagery speaks for itself.
 *
 * "Random" is locked in once per data fetch via useMemo so the tiles
 * don't reshuffle on every render — they only change when React Query
 * refetches (every 5 minutes, or on hard reload).
 *
 * Exterior is intentionally NOT shown here — the dedicated
 * `FacadesShowcase` section below already covers that use case.
 */
// Six-slot layout — the 1st and 4th tiles are double-height to add
// architectural rhythm.
const TILE_LAYOUT = ["row-span-2", "", "", "row-span-2", "", ""];
const HOME_TILES = 6;

export function Gallery() {
  const { data, isLoading, dataUpdatedAt } = useQuery({
    queryKey: ["public", "gallery", "interior", "all"],
    // Cap at 100 so the random pool is broad without pulling thousands.
    queryFn: () => fetchGallery({ section: "interior", limit: 100 }),
    staleTime: 5 * 60_000,
  });

  // Randomly pick up to HOME_TILES images. Stable per fetch cycle —
  // `dataUpdatedAt` only changes when React Query refreshes.
  const tiles = useMemo(() => {
    const pool = (data ?? []).filter((g) => g.image);
    if (pool.length === 0) return [];
    return shuffle(pool)
      .slice(0, HOME_TILES)
      .map((g) => ({ src: assetUrl(g.image), key: g._id, alt: g.title }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataUpdatedAt]);

  // If the admin hasn't published any interior photos yet, hide the
  // whole section rather than show stock fallbacks — keeps the home
  // page clean during the rollout.
  if (!isLoading && tiles.length === 0) return null;

  return (
    <section className="bg-bg-elevated py-20 lg:py-28">
      <Container>
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div className="max-w-2xl">
            <p className="eyebrow">Inside the home</p>
            <h2 className="mt-3 font-display text-4xl text-text-primary lg:text-5xl">
              Interior gallery
            </h2>
            <p className="mt-4 text-text-secondary">
              Kitchens, living rooms, ensuites, and joinery — the spaces you live in every day.
            </p>
          </div>
          <Link
            to="/gallery#interior"
            className="inline-flex items-center gap-1 text-sm text-accent hover:text-accent-hover"
          >
            View all interior images
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        {isLoading ? (
          <div className="grid auto-rows-[160px] grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:auto-rows-[200px]">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`animate-pulse rounded-2xl bg-bg-elevated-2 ${TILE_LAYOUT[i] ?? ""}`}
              />
            ))}
          </div>
        ) : (
          <div className="grid auto-rows-[160px] grid-cols-2 gap-3 md:grid-cols-3 md:gap-4 lg:auto-rows-[200px]">
            {tiles.map((t, i) => (
              <Link
                key={t.key}
                to="/gallery#interior"
                className={`group relative overflow-hidden rounded-2xl bg-bg-elevated-2 ${TILE_LAYOUT[i] ?? ""}`}
              >
                <ImageWithSkeleton
                  src={t.src}
                  alt={t.alt ?? ""}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Soft bottom fade for visual weight; no title overlay. */}
                <div className="absolute inset-0 bg-gradient-to-t from-bg-base/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </Link>
            ))}
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Link
            to="/gallery#interior"
            className="inline-flex items-center gap-2 rounded-md bg-accent px-6 py-3 text-sm font-medium text-bg-base transition-all hover:bg-accent-hover hover:shadow-[0_12px_32px_-8px_rgba(212,175,55,0.6)]"
          >
            View all interior images
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>
      </Container>
    </section>
  );
}

/**
 * Fisher–Yates shuffle. Returns a new array; never mutates the input.
 * Used to randomise the homepage tile picks each fetch cycle.
 */
function shuffle(arr) {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
